import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ControlledInput } from "@/components/ui/customs/controlled-input";
import GenericSelect from "@/components/ui/customs/generic-select";
import { GetCustomV1BoResponse, GetTarifResponseDto } from "@/types/api/api-types";
import { CustomFormData } from "@/types/forms/bo/custom.form";
import { Trash2Icon, Link } from "lucide-react";
import { useState } from "react";
import { Control, useFieldArray, UseFormWatch } from "react-hook-form";

type CustomTarifProps = {
  id: string;
  custom: GetCustomV1BoResponse | undefined;
  tarifs: GetTarifResponseDto | undefined;
  control: Control<CustomFormData>;
  watch: UseFormWatch<CustomFormData>;
  disabled?: boolean;
};

export const CustomTarif = ({
  id,
  custom,
  tarifs,
  control,
  watch,
  disabled: readOnly = false,
}: CustomTarifProps) => {
  const { t } = useTranslation("boParameters");
  const [selectedTarif, setSelectedTarif] = useState<string | undefined>(
    tarifs?.data.find((tarif) => tarif.isDefault)?.label,
  );
  const selectedTarifData = tarifs?.data.find((tarif) => tarif.label === selectedTarif);
  const isLinked = selectedTarifData?.followingId ? true : false;
  const disabled = readOnly || isLinked;
  const [rerender, setRerender] = useState(0);

  const customTarifsWatch = watch("customTarifs");

  // Find the index of the selected tarif
  const selectedTarifIndex = customTarifsWatch.findIndex((field) => field.label === selectedTarif);

  // Create a separate useFieldArray for bottleTransportPrices of the selected tarif
  const bottleTransportPricesFieldArray = useFieldArray({
    control,
    name: `customTarifs.${selectedTarifIndex}.bottleTransportPrices`,
  });

  const deleteBracket = (index: number) => {
    bottleTransportPricesFieldArray.remove(index);
    setRerender(rerender + 1);
  };

  const addBracket = () => {
    bottleTransportPricesFieldArray.append({
      quantity: 0,
      amount: 0,
    });
    setRerender(rerender + 1);
  };

  if ((id && !custom) || !tarifs) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row items-center gap-2">
        <p>{t("customTarif.tarifLabel")}</p>
        <GenericSelect
          options={tarifs.data.map((tarif) => ({
            label: tarif.label,
            value: tarif.label,
            icon: tarif.followingId ? <Link className="h-4 w-4" /> : null,
          }))}
          onChange={(value) => setSelectedTarif(value)}
          value={selectedTarif}
        />
      </div>
      <div className="flex flex-row justify-center gap-2">
        {customTarifsWatch.map((field, index) => (
          <div key={field.id + rerender} className={field.label !== selectedTarif ? "hidden" : ""}>
            {selectedTarifData && selectedTarifData.followingId && (
              <p className="text-sm text-gray-500">
                {t("customTarif.linkedTo", {
                  label: selectedTarifData.following.label,
                  percentage: selectedTarifData.changePercentage,
                })}
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <p>{t("customTarif.bottlesHeader")}</p>
              <p>{t("customTarif.valueHeader")}</p>
            </div>
            {field.bottleTransportPrices.map((_price, bottleIndex) => (
              <div key={bottleIndex} className="grid grid-cols-[1fr_1fr_auto] gap-2 pt-1">
                <ControlledInput
                  control={control}
                  name={`customTarifs.${index}.bottleTransportPrices.${bottleIndex}.quantity`}
                  inputType="number"
                  placeholder={t("customTarif.bottlesPlaceholder")}
                  disabled={disabled}
                  className={disabled ? "bg-gray-100" : ""}
                />
                <ControlledInput
                  control={control}
                  name={`customTarifs.${index}.bottleTransportPrices.${bottleIndex}.amount`}
                  inputType="number"
                  placeholder={t("customTarif.valuePlaceholder")}
                  disabled={disabled}
                  className={disabled ? "bg-gray-100" : ""}
                />
                {!disabled && (
                  <Button
                    variant="outline"
                    size="icon"
                    type="button"
                    onClick={() => deleteBracket(bottleIndex)}
                    disabled={disabled}
                  >
                    <Trash2Icon className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {!disabled && (
              <div className="mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addBracket()}
                  disabled={disabled}
                >
                  {t("customTarif.addBracket")}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
